import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import type { ThemeTextStep } from 'storybookConfig/stories/theme';
import theme from 'storybookConfig/stories/theme';

/**
 * Ensure that the values on this Enum correspond to the typography annotations in Figma.
 */
export enum TypographyVariants {
  // Headings

  H1 = 'Headings/H1',
  H2 = 'Headings/H2',
  H3 = 'Headings/H3',
  H4 = 'Headings/H4',

  // Body Text

  BodyRegular = 'Body/Regular',
  BodyRegularItalics = 'Body/Regular Italics',
  BodyRegularBold = 'Body/Regular Bold',
  BodyRegularUnderlined = 'Body/Regular Underlined',

  BodySmall = 'Body/Body Small',
  BodySmallItalics = 'Body/Body Small Italics',
  BodySmallBold = 'Body/Body Small Bold',
  BodySmallUnderlined = 'Body/Body Small Underlined',

  BodyHeader = 'Body/Header',
  BodyCaption = 'Body/Caption',

  // Navigation

  NavigationLink = 'Navigation/Nav Link',
  NavigationLinkActive = 'Navigation/Nav Link Active',

  // Buttons

  Button = 'Buttons/Button',
  ButtonSmall = 'Buttons/Button Small',

  // Inputs

  Input = 'Inputs/Input',
  InputMessage = 'Inputs/Input Message',

  // Chips

  ChipTextLarge = 'Chips/Chip Text Large',
  ChipTextSmall = 'Chips/Chip Text Small',

  // Other

  BadgeValue = 'Other/Badge Value',
  ColumnLabel = 'Other/Column Label',
  SectionLabel = 'Other/Section Label',
  MicroLabel = 'Other/Micro Label',
}

export type TypographyVariant = `${TypographyVariants}`;

/** One step of the type scale in the interface face. */
const text = (step: ThemeTextStep, weight: keyof typeof theme.fontWeight = 'regular') => css`
  font-family: ${theme.font.primary};
  font-style: normal;
  font-weight: ${theme.fontWeight[weight]};
  font-size: ${theme.text[step].size};
  line-height: ${theme.text[step].leading};
  letter-spacing: ${theme.tracking.text};
`;

const italic = css`
  font-style: italic;
`;

const underlined = css`
  text-decoration-line: underline;
`;

/* The one exception to sentence case: short uppercase labels, tracked open. */
const caps = (tracking: string) => css`
  letter-spacing: ${tracking};
  text-transform: uppercase;
`;

const variants: Record<TypographyVariant, SerializedStyles> = {
  'Headings/H1': text('h1'),
  'Headings/H2': text('h2'),
  'Headings/H3': text('h3', 'bold'),
  'Headings/H4': text('h4', 'bold'),

  'Body/Regular': text('body'),
  'Body/Regular Italics': css(text('body'), italic),
  'Body/Regular Bold': text('body', 'bold'),
  'Body/Regular Underlined': css(text('body'), underlined),

  'Body/Body Small': text('bodySm'),
  'Body/Body Small Italics': css(text('bodySm'), italic),
  'Body/Body Small Bold': text('bodySm', 'bold'),
  'Body/Body Small Underlined': css(text('bodySm'), underlined),

  'Body/Header': text('caption', 'bold'),
  'Body/Caption': text('caption'),

  'Navigation/Nav Link': text('control'),
  'Navigation/Nav Link Active': text('control', 'bold'),

  'Buttons/Button': text('control'),
  'Buttons/Button Small': text('controlSm'),

  'Inputs/Input': text('input'),
  'Inputs/Input Message': text('caption'),

  'Chips/Chip Text Large': text('chip'),
  'Chips/Chip Text Small': text('chipSm'),

  'Other/Badge Value': css(
    text('chipSm', 'bold'),
    css`
      font-variant-numeric: tabular-nums;
    `
  ),
  'Other/Column Label': css(text('columnLabel', 'bold'), caps(theme.tracking.caps)),
  'Other/Section Label': css(text('sectionLabel', 'bold'), caps(theme.tracking.capsWide)),
  'Other/Micro Label': text('micro'),
};

/**
 * A helper to apply proper typography to any Styled Component. A disabled state changes colour,
 * not type, so there are no disabled variants.
 */
const typography = (variant: TypographyVariant) => variants[variant];

export default typography;
